import { z } from 'zod';
import { CurrencySpec, Money } from './money.ts';
import { BetId, GameRoundId, PartnerWalletRoundId, SessionId, UtcInstant, WalletTransactionId } from './primitives.ts';

export const WalletOperation = z.enum(['DEBIT', 'CREDIT', 'ROLLBACK']);
export type WalletOperation = z.infer<typeof WalletOperation>;

/** UNKNOWN: request outcome not known (e.g. timeout). It is NOT a failure. */
export const WalletTxState = z.enum(['PENDING', 'SUCCEEDED', 'FAILED', 'UNKNOWN']);
export type WalletTxState = z.infer<typeof WalletTxState>;

export const WalletTransaction = z.object({
  transactionId: WalletTransactionId,
  operation: WalletOperation,
  betId: BetId,
  gameRoundId: GameRoundId,
  partnerWalletRoundId: PartnerWalletRoundId.nullable(),
  amount: Money,
  /** For ROLLBACK: the debit being reversed. */
  originalTransactionId: WalletTransactionId.nullable(),
  state: WalletTxState,
  /** Fingerprint of the economic payload for idempotency conflict detection. */
  payloadFingerprint: z.string().min(1),
  createdAt: UtcInstant,
});
export type WalletTransaction = z.infer<typeof WalletTransaction>;

export const Balance = z.object({
  balance: Money,
  balanceVersion: z.number().int().nonnegative(),
  asOf: UtcInstant,
  isDemo: z.boolean(),
});
export type Balance = z.infer<typeof Balance>;

/**
 * Player launch session. The wallet currency comes from the operator launch, never from the player's
 * country or display preference; a different wallet currency means a new session.
 */
export const LaunchSession = z.object({
  sessionId: SessionId,
  walletCurrency: CurrencySpec,
  startedAt: UtcInstant,
  /** Country hint (e.g. from the operator): may suggest language/display currency only. Not an entitlement. */
  countryHint: z.string().regex(/^[A-Z]{2}$/).nullable(),
  isDemo: z.boolean(),
  /** Fixed at launch: REAL = real money on the operator's wallet (real-money site); DEMO / FUN = play money. */
  moneyMode: z.enum(['DEMO', 'REAL', 'FUN']).optional(),
});
export type LaunchSession = z.infer<typeof LaunchSession>;
